import { ROUTES } from "@/lib/constants";

/**
 * Session flag that routes a sign-up through the creator (organization)
 * onboarding: OnboardingPage shows the creator variant with an org-name field
 * and continues to /onboarding/org, which creates the org and hands the goal
 * to the assistant.
 *
 * Lives here, not in RegisterQuestsPage, so the app entry can set it without
 * pulling a lazy route into the main bundle.
 */
export const VOLUNTEER_CREATOR_KEY = "wexare_volunteer_creator";

/**
 * "Need more hands?" posters (cafes, shops, community, faith, learning,
 * services, sports) carry a QR to
 * `https://wexare.com/?utm_source=poster&utm_medium=qr&utm_campaign=<venue>&utm_content=creators`.
 * They are printed, so the app must honour that link rather than the link
 * being changed.
 */
export function isCreatorCampaign(search: string): boolean {
  const content = new URLSearchParams(search).get("utm_content");
  if (!content) return false;
  const v = content.trim().toLowerCase();
  return v === "creators" || v === "creator";
}

/**
 * Where a creator poster link that points at the homepage should really land:
 * the organizations landing, whose CTA logs in and runs the org onboarding.
 * The UTM params ride along (analytics reads them off the URL) and `ref`
 * becomes `poster-<venue>`, which the landing reports as its source.
 *
 * Only "/" is rewritten: a creator link to a specific page keeps its page.
 */
export function creatorCampaignRedirect(pathname: string, search: string): string | null {
  if (!isCreatorCampaign(search) || pathname !== "/") return null;
  const params = new URLSearchParams(search);
  if (!params.get("ref")) {
    const source = params.get("utm_source")?.trim() || "campaign";
    const venue = params.get("utm_campaign")?.trim();
    params.set("ref", venue ? `${source}-${venue}` : source);
  }
  return `${ROUTES.ORGANIZATIONS}?${params.toString()}`;
}

/**
 * Run once at app boot, before the router mounts: tag the tab as a creator
 * (sessionStorage survives the Privy login) and, for the homepage link,
 * rewrite the URL in place so the router renders the organizations landing
 * without a visible bounce.
 */
export function captureCreatorCampaign(location: Location, history: History): boolean {
  if (!isCreatorCampaign(location.search)) return false;
  try {
    sessionStorage.setItem(VOLUNTEER_CREATOR_KEY, "1");
  } catch {
    /* storage unavailable — the landing sets it again after login */
  }
  const to = creatorCampaignRedirect(location.pathname, location.search);
  if (to) history.replaceState(history.state, "", `${to}${location.hash}`);
  return true;
}
